<template>
	<div
		class="two-slot-offer-card-detail template-layout premium-shop-item__content"
	>
		<div class="premium-shop-item__values">
			<ScrollFade :direction="ScrollDirection.HORIZONTAL">
				<template #scrollContent>
					<div class="premium-shop-item__rewards">
						<div v-if="firstSlotItems">
							<BuildingRewardsRow
								v-if="
									firstSlotGroup === CommodityGroup.BUILDING
								"
								:items="firstSlotItems as BuildingSlotItem[]"
								:context="RewardsGridContext.CARD"
								:template="contentData.attributes.template"
							/>
							<UpgradeRewardsRow
								v-if="firstSlotGroup === CommodityGroup.UPGRADE"
								:items="firstSlotItems as UpgradeSlotItem[]"
								:context="RewardsGridContext.CARD"
								:template="contentData.attributes.template"
							/>
							<ResourceOrUtilityRewardsGrid
								v-if="
									firstSlotGroup ===
										CommodityGroup.RESOURCE ||
									firstSlotGroup === CommodityGroup.UTILITY
								"
								:items="
									firstSlotItems as ResourceOrUtilitySlotItem[]
								"
								:context="RewardsGridContext.CARD"
							/>
						</div>
						<span class="premium-shop-item__plus-divider">+</span>
						<div v-if="secondSlotItems">
							<ResourceOrUtilityRewardsRow
								v-if="
									secondSlotGroup ===
										CommodityGroup.RESOURCE ||
									secondSlotGroup === CommodityGroup.UTILITY
								"
								:items="
									secondSlotItems as ResourceOrUtilitySlotItem[]
								"
								:context="RewardsGridContext.CARD"
							/>
							<EventResourceRewardsRow
								v-if="
									secondSlotGroup ===
									CommodityGroup.EVENT_RESOURCE
								"
								:items="
									secondSlotItems as EventResourceSlotItem[]
								"
								:context="RewardsGridContext.CARD"
							/>
						</div>
					</div>
				</template>
			</ScrollFade>
		</div>
	</div>
</template>

<script lang="ts" setup>
import ScrollFade from "@/components/ScrollFade.vue";
import BuildingRewardsRow from "@/components/shop/blocks/parts/BuildingRewardsRow.vue";
import EventResourceRewardsRow from "@/components/shop/blocks/parts/EventResourceRewardsRow.vue";
import ResourceOrUtilityRewardsGrid from "@/components/shop/blocks/parts/ResourceOrUtilityRewardsGrid.vue";
import ResourceOrUtilityRewardsRow from "@/components/shop/blocks/parts/ResourceOrUtilityRewardsRow.vue";
import UpgradeRewardsRow from "@/components/shop/blocks/parts/UpgradeRewardsRow.vue";
import type {
	BuildingSlotItem,
	EventResourceSlotItem,
	ResourceOrUtilitySlotItem,
	UpgradeSlotItem,
} from "@/types/shop";
import { CommodityGroup, type ShopItem } from "@/types/shop";
import { RewardsGridContext, ScrollDirection } from "@/types/ui";
import { computed } from "vue";

interface Props {
	contentData: ShopItem;
}

const { contentData } = defineProps<Props>();

const firstSlotGroup = computed((): CommodityGroup | null => {
	const firstSlot = contentData.attributes?.slots?.[0];
	const firstItem = firstSlot?.items?.[0];
	return firstItem?.group ?? null;
});

const secondSlotGroup = computed((): CommodityGroup | null => {
	const secondSlot = contentData.attributes?.slots?.[1];
	const secondItem = secondSlot?.items?.[0];
	return secondItem?.group ?? null;
});

const firstSlotItems = computed(
	():
		| Array<BuildingSlotItem>
		| Array<UpgradeSlotItem>
		| Array<ResourceOrUtilitySlotItem> => {
		return (
			(contentData.attributes?.slots?.[0]?.items as
				| Array<BuildingSlotItem>
				| Array<UpgradeSlotItem>
				| Array<ResourceOrUtilitySlotItem>) ?? null
		);
	},
);

const secondSlotItems = computed(
	(): Array<ResourceOrUtilitySlotItem> | Array<EventResourceSlotItem> => {
		return (
			(contentData.attributes?.slots?.[1]?.items as
				| Array<ResourceOrUtilitySlotItem>
				| Array<EventResourceSlotItem>) ?? null
		);
	},
);
</script>

<style lang="scss" scoped>
.premium-shop-item__content {
	position: relative;
	flex: 1 1 auto;
	width: 100%;
	min-height: 0;
	@include flex-row-center;
	height: 100%;

	.premium-shop-item {
		&__values {
			flex: 1 1 auto;
			@include flex-row-center;
			justify-content: flex-start;
			padding-bottom: 2.5rem;
			height: 100%;
			overflow: hidden;

			.scroll-fade-container {
				display: flex;

				:deep(.scrollbar__wrapper) {
					height: 100%;

					.scrollbar__scroller {
						height: 100%;

						.scrollbar__content.scrollbar__content--horizontal {
							width: 100%;
							height: 100%;
						}
					}
				}
			}
		}

		&__plus-divider {
			color: #f4d278;
			font-weight: 800;
			font-size: 2.5rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}

		&__rewards {
			@include flex-row-center;
			flex: 1 1 auto;
			gap: 0rem; // SLIM fix
		}
	}
}
</style>
